import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { fetchData } from "../../api";

export default function ProjectDetail() {
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);
    const { projectId } = useParams();

    useEffect(() => {
        const loadProject = async () => {
            try {
                const projects = await fetchData("our-projects-arrays", true);
                
                let foundProject = projects.find(p => p.slug === projectId);
                
                if (!foundProject) {
                    const index = parseInt(projectId);
                    if (!isNaN(index) && index >= 0 && index < projects.length) {
                        foundProject = projects[index];
                    }
                }
                
                setProject(foundProject);
            } catch (error) {
                console.error("Error loading project:", error);
            } finally {
                setLoading(false);
            }
        };

        loadProject();
    }, [projectId]);

    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                if (isVideoModalOpen) {
                    closeVideoModal();
                } else if (isImageModalOpen) {
                    closeImageModal();
                }
            }
        };

        if (isVideoModalOpen || isImageModalOpen) {
            document.addEventListener('keydown', handleEscKey);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
            document.body.style.overflow = 'unset';
        };
    }, [isVideoModalOpen, isImageModalOpen]);

    const openImageModal = (image) => {
        setSelectedImage(image);
        setIsImageModalOpen(true);
        setZoomLevel(1);
    };

    const closeImageModal = () => {
        setIsImageModalOpen(false);
        setSelectedImage(null);
        setZoomLevel(1);
    };

    const openVideoModal = () => {
        setIsVideoModalOpen(true);
    };

    const closeVideoModal = () => {
        setIsVideoModalOpen(false);
    };

    const zoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 0.25, 2));
    };

    const zoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
    };

    const resetZoom = () => {
        setZoomLevel(1);
    };

    const handleVideoModalClick = (e) => {
        if (e.target === e.currentTarget) {
            closeVideoModal();
        }
    };

    const handleImageModalClick = (e) => {
        if (e.target === e.currentTarget) {
            closeImageModal();
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#286766]"></div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">Project Not Found</h1>
                    <p className="text-gray-600">The project you're looking for doesn't exist.</p>
                </div>
            </div>
        );
    }

    const heroImgUrl = project.heroImg?.url || project.heroImg?.data?.attributes?.url;
    const videoUrl = project.Video?.url || project.Video?.data?.attributes?.url;

    return (
        <>
            <div className="min-h-screen bg-gray-50">
                {/* Hero Section */}
                <section className="relative h-96 md:h-[500px] overflow-hidden">
                    {heroImgUrl && (
                        <img
                            src={`${process.env.NEXT_PUBLIC_STRAPI_URL || "https://api.bouwbedrijfgeluk.nl"}${heroImgUrl}`}
                            alt={project.Title}
                            className="w-full h-full object-cover"
                        />
                    )}
                    <div className="absolute inset-0 bg-black/40"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center text-white">
                            <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{ color: 'white' }}>{project.Title}</h1>
                            <p className="text-xl md:text-2xl max-w-3xl mx-auto px-4" style={{ color: 'white' }}>
                                {project.Description}
                            </p>
                        </div>
                    </div>
                </section>

                {/* Project Content */}
                <div className="container mx-auto px-4 py-16">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                        {/* Main Content */}
                        <div className="lg:col-span-2 space-y-12">
                            {/* Video Section */}
                            {videoUrl && (
                                <section>
                                    <h2 className="text-3xl font-bold text-[#286766] mb-6">
                                        Project Video
                                    </h2>
                                    <div className="relative group cursor-pointer" onClick={openVideoModal}>
                                        <div className="relative rounded-lg overflow-hidden shadow-lg">
                                            <video
                                                src={`${process.env.NEXT_PUBLIC_STRAPI_URL || "https://api.bouwbedrijfgeluk.nl"}${videoUrl}`}
                                                className="w-full h-64 md:h-96 object-contain bg-black"
                                                poster={heroImgUrl ? `${process.env.NEXT_PUBLIC_STRAPI_URL || "https://api.bouwbedrijfgeluk.nl"}${heroImgUrl}` : undefined}
                                            />
                                            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-all duration-300 flex items-center justify-center">
                                                <div className="bg-white/90 backdrop-blur-sm rounded-full p-4 group-hover:scale-110 transition-transform duration-300">
                                                    <svg className="w-8 h-8 text-gray-800" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M8 5v14l11-7z"/>
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            )}

                            {/* Overview Section */}
                            {project.overviewHeading && (
                                <section>
                                    <h2 className="text-3xl font-bold text-[#286766] mb-6">
                                        {project.overviewHeading}
                                    </h2>
                                    <div className="space-y-4 text-gray-700 leading-relaxed">
                                        {project.overviewPara1 && (
                                            <p>{project.overviewPara1}</p>
                                        )}
                                        {project.overviewPara2 && (
                                            <p>{project.overviewPara2}</p>
                                        )}
                                    </div>
                                </section>
                            )}

                            {/* Gallery Section */}
                            {project.galleryImages && project.galleryImages.length > 0 && (
                                <section>
                                    {project.galleryHeading && (
                                        <h2 className="text-3xl font-bold text-[#286766] mb-6">
                                            {project.galleryHeading}
                                        </h2>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {project.galleryImages.map((img, index) => {
                                            const imgUrl = img.url || img?.data?.attributes?.url;
                                            return (
                                                <div 
                                                    key={index} 
                                                    className="group relative rounded-lg overflow-hidden shadow-lg cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-xl"
                                                    onClick={() => openImageModal(imgUrl)}
                                                >
                                                    <img
                                                        src={`${process.env.NEXT_PUBLIC_STRAPI_URL || "https://api.bouwbedrijfgeluk.nl"}${imgUrl}`}
                                                        alt={`${project.Title} - Image ${index + 1}`}
                                                        className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                                                    />
                                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-full p-3">
                                                            <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </section>
                            )}

                            {/* Highlights Section */}
                            {project.highlightsList && project.highlightsList.length > 0 && (
                                <section>
                                    {project.highlightsHeading && (
                                        <h2 className="text-3xl font-bold text-[#286766] mb-6">
                                            {project.highlightsHeading}
                                        </h2>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {project.highlightsList.map((highlight, index) => (
                                            <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-md border-l-4 border-[#286766]">
                                                <div className="flex-shrink-0 w-6 h-6 bg-[#286766] rounded-full flex items-center justify-center mt-0.5">
                                                    <span className="text-white text-sm font-bold">{index + 1}</span>
                                                </div>
                                                <p className="text-gray-700 leading-relaxed">{highlight.point}</p>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-8">
                            {/* Project Specifications */}
                            {(project.client || project.location || project.area || project.duration || project.architect || project.status) && (
                                <div className="bg-white p-6 rounded-lg shadow-md">
                                    {project.specsHeading && (
                                        <h3 className="text-2xl font-bold text-[#286766] mb-6">
                                            {project.specsHeading}
                                        </h3>
                                    )}
                                    <div className="space-y-4">
                                        {project.client && (
                                            <div>
                                                <span className="font-semibold text-gray-800">Klant:</span>
                                                <p className="text-gray-600">{project.client}</p>
                                            </div>
                                        )}
                                        {project.location && (
                                            <div>
                                                <span className="font-semibold text-gray-800">Location:</span>
                                                <p className="text-gray-600">{project.location}</p>
                                            </div>
                                        )}
                                        {project.area && (
                                            <div>
                                                <span className="font-semibold text-gray-800">Area:</span>
                                                <p className="text-gray-600">{project.area}</p>
                                            </div>
                                        )}
                                        {project.duration && (
                                            <div>
                                                <span className="font-semibold text-gray-800">Duration:</span>
                                                <p className="text-gray-600">{project.duration}</p>
                                            </div>
                                        )}
                                        {project.architect && (
                                            <div>
                                                <span className="font-semibold text-gray-800">Architect:</span>
                                                <p className="text-gray-600">{project.architect}</p>
                                            </div>
                                        )}
                                        {project.status && (
                                            <div>
                                                <span className="font-semibold text-gray-800">Status:</span>
                                                <p className="text-gray-600">{project.status}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* CTA Button */}
                            {project.ctaLink && (
                                <div className="bg-gradient-to-r from-[#286766] to-[#27352C] p-6 rounded-lg text-white text-center">
                                    <h3 className="text-xl font-bold mb-4">Interested in this project?</h3>
                                    <a
                                        href={project.ctaLink}
                                        className="inline-block bg-white text-[#286766] font-semibold py-3 px-6 rounded-lg hover:bg-gray-100 transition-colors duration-300"
                                    >
                                        {project.ctaText || "Get in Touch"}
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Video Modal */}
            {isVideoModalOpen && videoUrl && (
                <div 
                    className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={handleVideoModalClick}
                >
                    <div className="relative w-full max-w-4xl">
                        <button
                            onClick={closeVideoModal}
                            className="absolute top-4 right-4 z-10 bg-black/50 text-white rounded-full p-3 hover:bg-black/70 transition-colors duration-200"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <video
                            src={`${process.env.NEXT_PUBLIC_STRAPI_URL || "https://api.bouwbedrijfgeluk.nl"}${videoUrl}`}
                            className="w-full h-auto rounded-lg"
                            controls
                            autoPlay
                        />
                    </div>
                </div>
            )}

            {/* Image Modal with Improved Zoom */}
            {isImageModalOpen && selectedImage && (
                <div 
                    className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={handleImageModalClick}
                >
                    <div className="relative w-full h-full flex items-center justify-center">
                        <button
                            onClick={closeImageModal}
                            className="absolute top-4 right-4 z-10 bg-black/50 text-white rounded-full p-3 hover:bg-black/70 transition-colors duration-200"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className="absolute top-4 left-4 z-10 flex gap-2">
                            <button
                                onClick={zoomOut}
                                className="bg-black/50 text-white rounded-full p-3 hover:bg-black/70 transition-colors duration-200"
                                disabled={zoomLevel <= 0.5}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                </svg>
                            </button>
                            <button
                                onClick={zoomIn}
                                className="bg-black/50 text-white rounded-full p-3 hover:bg-black/70 transition-colors duration-200"
                                disabled={zoomLevel >= 2}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                            </button>
                            <button
                                onClick={resetZoom}
                                className="bg-black/50 text-white rounded-full p-3 hover:bg-black/70 transition-colors duration-200"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                </svg>
                            </button>
                        </div>

                        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                            {Math.round(zoomLevel * 100)}%
                        </div>

                        <div className="w-full h-full flex items-center justify-center overflow-auto">
                            <img
                                src={`${process.env.NEXT_PUBLIC_STRAPI_URL || "https://api.bouwbedrijfgeluk.nl"}${selectedImage}`}
                                alt="Zoomed Image"
                                className="max-w-full max-h-full object-contain transition-transform duration-300"
                                style={{ transform: `scale(${zoomLevel})` }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
} 